// Packages
import React, { useState, useCallback } from 'react';

// Components
import { Box } from 'grommet';

import { getPaywallFinePrintCopy } from './paywall.copy';

// Hooks
import { usePaywallTracking } from '@hooks/revcat/usePaywallTracking';

// Types
import {
  PackageName,
  PaywallEvents,
  PaywallProps,
  SubscriptionOffering,
  SubscriptionTerms,
  Terms,
} from './paywall.types';

import SHDModal from '@ui/Modal/SHDModal';
import SHDButton from '@ui/Button/SHDButton';
import SHDText from '@ui/Text/SHDText';
import SHDImage from '@ui/Image/SHDImage';
import SubscriptionTermOption from './components/SubscriptionTermsOption';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';

const Paywall: React.FC<PaywallProps> = ({
  context,
  playerId = '',
  isOpen,
  onClose,
  onUnlockAccess,
  prices,
}) => {
  // Hooks
  const { trackPaywallEvent } = usePaywallTracking(context, playerId);
  const {
    monthly: monthlyPrice,
    annual: annualPrice,
    slhdOfferings: offerings,
  } = prices;

  // States
  const [termsSelected, setTermsSelected] = useState<SubscriptionOffering>({
    package: PackageName.PRO,
    price: monthlyPrice,
    term: Terms.MONTHLY,
  });

  // Handlers
  const handleTermsSelected = useCallback(
    (term: SubscriptionTerms) => {
      trackPaywallEvent(PaywallEvents.OFFERING_SELECTED, {
        context: context,
        offeringSelected: term,
      });
      setTermsSelected({
        package: PackageName.PRO,
        price: term === Terms.MONTHLY ? monthlyPrice : annualPrice,
        term: term,
      });
    },
    [monthlyPrice, annualPrice, context, trackPaywallEvent]
  );

  const handleUnlockAccess = useCallback(() => {
    trackPaywallEvent(PaywallEvents.UNLOCK_ACCESS_CLICKED, {
      context: context,
    });
    onUnlockAccess(termsSelected, offerings);
  }, [termsSelected, offerings, context, trackPaywallEvent, onUnlockAccess]);

  const featureItems = [
    {
      icon: '/paywall/youtube.svg',
      title: 'Full access to all player videos',
      description:
        'Access all player videos including clips, highlights, Hitscope and reels.',
    },
    {
      icon: '/paywall/thunder.svg',
      title: 'Instant alerts',
      description:
        'Get game start notifications and instant access to play clips.',
    },
    {
      icon: '/paywall/five.svg',
      title: 'Free for up to 5 seats',
      description:
        'Instant alerts for up to 5 additional phone numbers without extra charge.',
    },
    {
      icon: '/paywall/person.svg',
      title: 'Public profile',
      description:
        'Allow anyone to watch and discover your player’s highlights.',
    },
  ];

  return (
    <>
      {isOpen && (
        <SHDThemeOverride full={false}>
          <SHDModal onClose={onClose} isTransparentControls>
            <SHDModalContainer horizontal={false}>
              <Box style={{ position: 'relative', overflow: 'visible' }}>
                <Box
                  background="#fff"
                  height={{ max: '60dvh' }}
                  style={{
                    position: 'relative',
                    overflow: 'visible',
                  }}
                  pad={{ top: 'large', horizontal: 'xxsmall' }}
                  gap="large"
                >
                  <img
                    src="/paywall/bg_ellipse.png"
                    alt="Glow background"
                    style={{
                      position: 'absolute',
                      top: -70,
                      left: 0,
                      width: '100%',
                      height: 'auto',
                      zIndex: 0,
                      pointerEvents: 'none',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    align="center"
                    height={{ min: '60px' }}
                    style={{
                      zIndex: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <SHDImage
                      src="/paywall/pro_primary.svg"
                      alt="pro-logo"
                      width="auto"
                    />
                  </Box>
                  <Box
                    className="paywall__features"
                    height={{ height: '305px' }}
                    gap="xsmall"
                    pad={{ vertical: 'large', horizontal: 'xxsmall' }}
                  >
                    {featureItems.map(({ icon, title, description }) => (
                      <Box
                        key={title}
                        direction="row"
                        align="center"
                        gap="small"
                        width="100%"
                        height={{ min: '60px', max: '66px', height: 'auto' }}
                        pad={{ vertical: 'xsmall' }}
                        style={{ zIndex: 1, marginTop: 'medium' }}
                      >
                        <Box
                          width="38px"
                          height="38px"
                          round="full"
                          background={{
                            image: "url('/paywall/ellipse.svg')",
                            size: 'cover',
                            position: 'center',
                          }}
                          justify="center"
                          align="center"
                          flex={{ shrink: 0 }}
                        >
                          <SHDImage src={icon} width="auto" alt={title} />
                        </Box>

                        <Box>
                          <SHDText style="body-semibold">{title}</SHDText>
                          <SHDText style="body" color="gray">
                            {description}
                          </SHDText>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </SHDModalContainer>
            <SHDFooter>
              <SHDModalContainer horizontal={false}>
                <Box
                  background="#fff"
                  pad={{
                    horizontal: 'medium',
                  }}
                  gap="small"
                >
                  <Box
                    direction="row"
                    gap="xxsmall"
                    justify="between"
                    wrap
                    width="100%"
                  >
                    <Box flex={{ grow: 1 }} basis="0">
                      <SubscriptionTermOption
                        id="monthly"
                        label="Monthly"
                        price={monthlyPrice}
                        suffix="month"
                        isActive={termsSelected.term === Terms.MONTHLY}
                        onSelected={() => handleTermsSelected(Terms.MONTHLY)}
                      />
                    </Box>

                    <Box flex={{ grow: 1 }} basis="0">
                      <SubscriptionTermOption
                        id="annually"
                        label="Annually"
                        price={annualPrice}
                        suffix="year"
                        tag="BEST VALUE"
                        isActive={termsSelected.term === Terms.ANNUAL}
                        onSelected={() => handleTermsSelected(Terms.ANNUAL)}
                      />
                    </Box>
                  </Box>

                  <SHDText style="legal" color="gray" textAlign="center">
                    {getPaywallFinePrintCopy(
                      termsSelected.price || monthlyPrice
                    )}
                  </SHDText>

                  <SHDButton
                    label={`Subscribe for ${
                      termsSelected.term === Terms.ANNUAL
                        ? '$35.99/year'
                        : '$5.99/month'
                    }`}
                    primary
                    onClick={() => handleUnlockAccess()}
                  />

                  <SHDText
                    style="footnote-semibold"
                    textAlign="center"
                    color="gray"
                  >
                    <a
                      href="/terms-of-service"
                      style={{ color: 'green', textDecorationLine: 'none' }}
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="/privacy"
                      style={{ color: 'green', textDecorationLine: 'none' }}
                    >
                      Privacy Policy
                    </a>
                  </SHDText>
                </Box>
              </SHDModalContainer>
            </SHDFooter>
          </SHDModal>
        </SHDThemeOverride>
      )}
    </>
  );
};

export default Paywall;
